import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Switch } from 'app/components/ui/switch';
import { Badge } from 'app/components/ui/badge';
import { Button } from 'app/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'app/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'app/components/ui/alert-dialog';
import { useToast } from 'app/components/ui/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import {
  Pencil,
  Eye,
  MoreHorizontal,
  Plus,
  Trash2,
  Smartphone,
  BanknoteIcon,
  QrCode,
  User,
  Info,
} from 'lucide-react';
import { useOfflinePaymentSlice, selectOfflinePaymentState } from './slice';
import {
  OfflinePaymentMethod,
  OfflinePaymentMethodFormData,
} from '../../types/offlinePayment';
import OfflinePaymentForm from './OfflinePaymentForm.tsx';

const iconMap = {
  Smartphone: Smartphone,
  BanknoteIcon: BanknoteIcon,
  QrCode: QrCode,
  User: User,
  Info: Info,
};

export default function OfflinePaymentMethods() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const offlinePaymentState = useSelector(selectOfflinePaymentState);

  const [modal, setModal] = React.useState<{
    open: boolean;
    mode: 'view' | 'edit' | 'add';
    method: OfflinePaymentMethod | null;
    category: 'indian' | 'worldwide';
    methodType: string;
  }>({
    open: false,
    mode: 'view',
    method: null,
    category: 'indian',
    methodType: '',
  });

  const [deleteConfirmModal, setDeleteConfirmModal] = React.useState<{
    open: boolean;
    method: OfflinePaymentMethod | null;
    category: 'indian' | 'worldwide';
    methodType: string;
  }>({
    open: false,
    method: null,
    category: 'indian',
    methodType: '',
  });

  // Use the slice hook
  const {
    useGetAllOfflinePaymentMethodsQuery,
    useUpdateOfflinePaymentMethodMutation,
    useAddOfflinePaymentMethodMutation,
    useDeleteOfflinePaymentMethodMutation,
    useToggleOfflinePaymentMethodStatusMutation,
  } = useOfflinePaymentSlice();

  // API hooks
  const {
    data: paymentMethods = { indian: {}, worldwide: {} },
    isLoading,
    error,
    refetch,
  } = useGetAllOfflinePaymentMethodsQuery();

  const [updatePaymentMethod, { isLoading: isUpdating }] =
    useUpdateOfflinePaymentMethodMutation();
  const [addPaymentMethod, { isLoading: isAdding }] =
    useAddOfflinePaymentMethodMutation();
  const [deletePaymentMethod, { isLoading: isDeleting }] =
    useDeleteOfflinePaymentMethodMutation();
  const [togglePaymentMethodStatus, { isLoading: isToggling }] =
    useToggleOfflinePaymentMethodStatusMutation();

  // Handle form submission
  const handleFormSubmit = async (formData: OfflinePaymentMethodFormData) => {
    try {
      if (modal.mode === 'edit') {
        await updatePaymentMethod({
          category: modal.category,
          methodType: modal.methodType,
          title: formData.title,
          icon: formData.icon,
          badge: formData.badge,
          details: formData.details,
        }).unwrap();
        toast({
          title: 'Payment method updated successfully',
          variant: 'success',
        });
      } else if (modal.mode === 'add') {
        await addPaymentMethod({
          category: formData.category,
          methodType: formData.methodType,
          paymentMethod: {
            id: `${formData.category}-${formData.methodType}`,
            title: formData.title,
            icon: formData.icon,
            badge: formData.badge,
            category: formData.category,
            details: formData.details,
          },
        }).unwrap();
        toast({
          title: 'Payment method added successfully',
          variant: 'success',
        });
      }

      setModal({
        open: false,
        mode: 'view',
        method: null,
        category: 'indian',
        methodType: '',
      });
      refetch();
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.message || 'Failed to save payment method';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteConfirmModal.method) return;

    try {
      await deletePaymentMethod({
        category: deleteConfirmModal.category,
        methodType: deleteConfirmModal.methodType,
      }).unwrap();

      toast({
        title: 'Payment method deleted successfully',
        variant: 'success',
      });

      setDeleteConfirmModal({
        open: false,
        method: null,
        category: 'indian',
        methodType: '',
      });
      refetch();
    } catch (error: any) {
      const errorMessage =
        error?.data?.error ||
        error?.message ||
        'Failed to delete payment method';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (
    method: OfflinePaymentMethod,
    category: 'indian' | 'worldwide',
    methodType: string,
    isActive: boolean,
  ) => {
    try {
      await togglePaymentMethodStatus({
        category,
        methodType,
        is_active: isActive,
      }).unwrap();

      toast({
        title: 'Status updated successfully',
        description: `Payment method ${isActive ? 'activated' : 'deactivated'}`,
        variant: 'success',
      });

      refetch();
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.message || 'Failed to update status';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  // Open modal handlers
  const openViewModal = (
    method: OfflinePaymentMethod,
    category: 'indian' | 'worldwide',
    methodType: string,
  ) => {
    setModal({
      open: true,
      mode: 'view',
      method,
      category,
      methodType,
    });
  };

  const openEditModal = (
    method: OfflinePaymentMethod,
    category: 'indian' | 'worldwide',
    methodType: string,
  ) => {
    setModal({
      open: true,
      mode: 'edit',
      method,
      category,
      methodType,
    });
  };

  const openAddModal = (category: 'indian' | 'worldwide') => {
    setModal({
      open: true,
      mode: 'add',
      method: null,
      category,
      methodType: '',
    });
  };

  const openDeleteModal = (
    method: OfflinePaymentMethod,
    category: 'indian' | 'worldwide',
    methodType: string,
  ) => {
    setDeleteConfirmModal({
      open: true,
      method,
      category,
      methodType,
    });
  };

  // Render payment method row
  const renderPaymentMethodRow = (
    method: OfflinePaymentMethod,
    category: 'indian' | 'worldwide',
    methodType: string,
  ) => {
    const IconComponent =
      iconMap[method.icon as keyof typeof iconMap] || Smartphone;

    return (
      <TableRow key={method.id}>
        {/* Icon Column */}
        <TableCell className="px-4 py-4">
          <div className="flex justify-center">
            <div
              className={`p-3 rounded-xl ${category === 'indian' ? 'bg-green-100' : 'bg-blue-100'
                }`}
            >
              <IconComponent
                className={`w-6 h-6 ${category === 'indian' ? 'text-green-600' : 'text-blue-600'
                  }`}
              />
            </div>
          </div>
        </TableCell>

        {/* Title Column */}
        <TableCell className="px-4 py-4">
          <h3 className="font-medium text-sm text-gray-900">
            {method.title || 'Untitled Payment Method'}
          </h3>
        </TableCell>

        {/* Badge Column */}
        <TableCell className="px-4 py-4 text-center">
          <Badge variant="secondary" className="text-xs px-2 py-1">
            {method.badge}
          </Badge>
        </TableCell>

        {/* Method Type Column */}
        <TableCell className="px-4 py-4 text-center">
          <span className="text-sm font-medium text-gray-700 capitalize">
            {methodType}
          </span>
        </TableCell>

        {/* Category Column */}
        <TableCell className="px-4 py-4">
          <Badge
            className={`text-xs px-2 py-1 ${category === 'indian'
                ? 'bg-green-100 text-green-800'
                : 'bg-blue-100 text-blue-800'
              }`}
          >
            {category === 'indian' ? 'Indian' : 'Worldwide'}
          </Badge>
        </TableCell>

        {/* Details Column */}
        <TableCell className="px-4 py-4">
          <div className="space-y-2">
            {method.details.upiId && (
              <div className="flex items-start gap-2 text-sm">
                <span className="text-gray-500 font-medium min-w-[60px] flex-shrink-0">
                  UPI:
                </span>
                <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono break-all">
                  {method.details.upiId}
                </code>
              </div>
            )}
            {method.details.walletAddress && (
              <div className="flex items-start gap-2 text-sm">
                <span className="text-gray-500 font-medium min-w-[60px] flex-shrink-0">
                  Wallet:
                </span>
                <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono break-all">
                  {method.details.walletAddress}
                </code>
              </div>
            )}
            {method.details.recipientName && (
              <div className="flex items-start gap-2 text-sm">
                <span className="text-gray-500 font-medium min-w-[60px] flex-shrink-0">
                  Recipient:
                </span>
                <span className="text-gray-800 text-sm font-medium">
                  {method.details.recipientName}
                </span>
              </div>
            )}
            {method.details.qrCodeImage && (
              <div className="flex items-center gap-2 text-sm">
                <QrCode className="w-4 h-4 text-green-600" />
                <span className="text-green-600 text-xs font-medium">
                  QR Code available
                </span>
              </div>
            )}
          </div>
        </TableCell>

        {/* Status Column */}
        <TableCell className="px-4 py-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <Switch
              checked={method.is_active !== false}
              onCheckedChange={checked =>
                handleToggleStatus(method, category, methodType, checked)
              }
              disabled={isToggling}
            />
            <Badge
              variant={method.is_active !== false ? 'default' : 'secondary'}
              className="text-xs"
            >
              {method.is_active !== false ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </TableCell>

        {/* Actions Column */}
        <TableCell className="px-4 py-4">
          <div className="flex items-center justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => openViewModal(method, category, methodType)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => openEditModal(method, category, methodType)}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => openDeleteModal(method, category, methodType)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading payment methods...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Error Loading Payment Methods
          </h2>
          <p className="text-gray-600 mb-4">
            Failed to load offline payment methods
          </p>
          <Button onClick={() => refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Offline Payment Methods</h1>
          <p className="text-gray-600 mt-1">
            Manage offline payment methods for Indian and worldwide users
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => openAddModal('indian')}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Indian Method
          </Button>
          <Button
            onClick={() => openAddModal('worldwide')}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Worldwide Method
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-[80px] px-4 py-4 text-center font-semibold text-gray-700">
                Icon
              </TableHead>
              <TableHead className="min-w-[150px] px-4 py-4 font-semibold text-gray-700">
                Title
              </TableHead>
              <TableHead className="w-[100px] px-4 py-4 text-center font-semibold text-gray-700">
                Badge
              </TableHead>
              <TableHead className="w-[120px] px-4 py-4 text-center font-semibold text-gray-700">
                Method Type
              </TableHead>
              <TableHead className="w-[120px] px-4 py-4 text-center font-semibold text-gray-700">
                Category
              </TableHead>
              <TableHead className="min-w-[250px] px-4 py-4 font-semibold text-gray-700">
                Payment Details
              </TableHead>
              <TableHead className="w-[100px] px-4 py-4 text-center font-semibold text-gray-700">
                Status
              </TableHead>
              <TableHead className="w-[140px] px-4 py-4 text-center font-semibold text-gray-700">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(!paymentMethods.indian ||
              Object.keys(paymentMethods.indian).length === 0) &&
              (!paymentMethods.worldwide ||
                Object.keys(paymentMethods.worldwide).length === 0) ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center text-gray-500 py-8"
                >
                  No offline payment methods configured
                </TableCell>
              </TableRow>
            ) : (
              <>
                {paymentMethods.indian &&
                  Object.entries(paymentMethods.indian).map(
                    ([methodType, method]) =>
                      renderPaymentMethodRow(method, 'indian', methodType),
                  )}
                {paymentMethods.worldwide &&
                  Object.entries(paymentMethods.worldwide).map(
                    ([methodType, method]) =>
                      renderPaymentMethodRow(method, 'worldwide', methodType),
                  )}
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {/* View/Edit/Add Modal */}
      <Dialog
        open={modal.open}
        onOpenChange={open => setModal(prev => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {modal.mode === 'view' && 'View Payment Method'}
              {modal.mode === 'edit' && 'Edit Payment Method'}
              {modal.mode === 'add' && 'Add Payment Method'}
            </DialogTitle>
          </DialogHeader>

          <OfflinePaymentForm
            mode={modal.mode}
            method={modal.method}
            category={modal.category}
            methodType={modal.methodType}
            onSubmit={handleFormSubmit}
            onCancel={() => setModal(prev => ({ ...prev, open: false }))}
            isLoading={isUpdating || isAdding}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <AlertDialog
        open={deleteConfirmModal.open}
        onOpenChange={open =>
          setDeleteConfirmModal(prev => ({ ...prev, open }))
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment Method</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "
              {deleteConfirmModal.method?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
